import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, EmptyState, PrimaryButton, GhostButton, Modal, Input, Select, Textarea, Table } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Store, Plus, Check, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", category: "facility", sport: "", city: "", state: "", contact_name: "", contact_phone: "", contact_email: "", description: "" };

export default function Providers() {
  const { hasRole } = useAuth();
  const [list, setList] = useState(null);
  const [sports, setSports] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const canVerify = hasRole("ops", "admin");
  const load = () => api.get("/providers").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); api.get("/sports").then((r) => setSports(r.data)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post("/providers", form); toast.success("Listing submitted - pending verification"); setOpen(false); setForm(EMPTY); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };
  const verify = async (id, status) => {
    try { await api.post(`/providers/${id}/verify`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (!list) return <Spinner />;

  return (
    <div className="animate-fadeUp">
      <PageHeader title="Providers & Facilities" subtitle="Venues, coaches, transport and accommodation partners"
        action={<PrimaryButton data-testid="add-provider-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Add listing</PrimaryButton>} />

      {list.length === 0 ? (
        <EmptyState icon={Store} title="No providers yet" text="Add a facility, coach, transport or accommodation listing."
          action={<PrimaryButton onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Add listing</PrimaryButton>} />
      ) : (
        <Table head={["Name", "Category", "Sport", "Location", "Status", canVerify ? "Actions" : ""].filter(Boolean)}>
          {list.map((p) => (
            <tr key={p.id} data-testid={`provider-row-${p.id}`} className="border-b border-border/60 last:border-0 hover:bg-background/40">
              <td className="px-4 py-3 font-medium">{p.name}</td>
              <td className="px-4 py-3 capitalize text-muted-foreground">{p.category}</td>
              <td className="px-4 py-3 text-muted-foreground">{p.sport || "-"}</td>
              <td className="px-4 py-3 text-muted-foreground">{p.city}, {p.state}</td>
              <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
              {canVerify && (
                <td className="px-4 py-3">
                  {p.status !== "verified" && (
                    <div className="flex gap-2">
                      <button data-testid={`verify-provider-${p.id}`} onClick={() => verify(p.id, "verified")} className="rounded-lg bg-emerald-500/15 p-1.5 text-emerald-300 hover:bg-emerald-500/25"><Check className="h-4 w-4" /></button>
                      <button onClick={() => verify(p.id, "rejected")} className="rounded-lg bg-rose-500/15 p-1.5 text-rose-300 hover:bg-rose-500/25"><X className="h-4 w-4" /></button>
                    </div>
                  )}
                </td>
              )}
            </tr>
          ))}
        </Table>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add provider / facility" wide>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
          <Input label="Name" testId="prov-name" required value={form.name} onChange={set("name")} className="sm:col-span-2" />
          <Select label="Category" testId="prov-category" value={form.category} onChange={set("category")}>
            <option value="facility">Facility / Venue</option><option value="coach">Coach / Academy</option>
            <option value="transport">Transport</option><option value="accommodation">Accommodation</option>
            <option value="provider">Event Provider</option>
          </Select>
          <Select label="Sport (optional)" testId="prov-sport" value={form.sport} onChange={set("sport")}>
            <option value="">-</option>{sports.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
          <Input label="City" testId="prov-city" required value={form.city} onChange={set("city")} />
          <Input label="State" testId="prov-state" required value={form.state} onChange={set("state")} />
          <Input label="Contact name" testId="prov-contact-name" required value={form.contact_name} onChange={set("contact_name")} />
          <Input label="Contact phone" testId="prov-contact-phone" required value={form.contact_phone} onChange={set("contact_phone")} />
          <Input label="Contact email" testId="prov-contact-email" type="email" required value={form.contact_email} onChange={set("contact_email")} className="sm:col-span-2" />
          <Textarea label="Description" testId="prov-desc" rows={2} value={form.description} onChange={set("description")} className="sm:col-span-2" />
          <div className="flex justify-end gap-2 sm:col-span-2">
            <GhostButton type="button" onClick={() => setOpen(false)}>Cancel</GhostButton>
            <PrimaryButton data-testid="save-provider-btn" type="submit" disabled={saving}>Submit</PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
